import { useContext, useMemo } from "react";

import { FlowsheetConstants } from "@/Flowsheets/FlowsheetConstants";
import { getFlowsheetCareElementValidationRegex } from "@/Flowsheets/Helpers/NumberCareElementHelper";
import { CareComponentConfigurationContext } from "@/Flowsheets/Models/CareComponentConfigurationContext";
import { CareElementOperatorType } from "@/Flowsheets/Models/CareElementOperatorType";
import { careElementUnitMapper } from "@/Flowsheets/Models/CareElements/CareElementsUnitMapper";
import { LabResultsCareElementConfig } from "@/Flowsheets/Models/CareElements/LabResultsCareElementConfig";
import { ReviewedCareElementConfig } from "@/Flowsheets/Models/CareElements/ReviewedCareElementConfig";
import { VitalSignsCareElementConfig } from "@/Flowsheets/Models/CareElements/VitalSignsCareElementConfig";
import { NumberInput } from "./NumberInput";
import { NumberInputWithDecimals } from "./NumberInputWIthDecimals";

import styles from "./../CareElementsConfiguration.module.scss";

function CareElementTargetInput({
    dataCy,
    element
}: Readonly<{
    dataCy?: string;
    element: LabResultsCareElementConfig | VitalSignsCareElementConfig;
}>): JSX.Element {
    const { onEdit, isCareElementInvalid } = useContext(CareComponentConfigurationContext);

    return (
        <div className={styles.targetContainer}>
            <NumberInput
                value={element.TargetRange?.LowerBound}
                onChange={value => onEdit(element.Name, { ...element, TargetRange: { ...element.TargetRange, LowerBound: value } })}
                dataCy={`${dataCy}-lowbound-input`}
                isInvalid={isCareElementInvalid(element.Name)}
            />
            {element.Operator == CareElementOperatorType.Between && (
                <>
                    <span>&</span>
                    <NumberInput
                        value={element.TargetRange?.UpperBound}
                        onChange={value => onEdit(element.Name, { ...element, TargetRange: { ...element.TargetRange, UpperBound: value } })}
                        dataCy={`${dataCy}-upperbound-input`}
                        isInvalid={isCareElementInvalid(element.Name)}
                    />
                </>
            )}
            <p className={styles.unitText}>{careElementUnitMapper[element.Name]}</p>
        </div>
    );
}

function CareElementSimpleTargetInput({
    dataCy,
    disabled = false,
    element,
    type = "percentage"
}: Readonly<{
    dataCy?: string;
    disabled?: boolean;
    element: ReviewedCareElementConfig;
    type?: "percentage" | "integer";
}>): JSX.Element {
    const { onEdit, isCareElementInvalid } = useContext(CareComponentConfigurationContext);

    const regularExpression = useMemo(
        () => getFlowsheetCareElementValidationRegex(type),
        [type]
    );

    return (
        <div className={styles.targetContainer}>
            <NumberInputWithDecimals
                numberValue={(element.Target as number | null) ?? null}
                regex={regularExpression}
                onChange={(stringValue, numberValue) =>
                    onEdit(element.Name, { ...element, Target: numberValue })
                }
                dataCy={`${dataCy}-lowbound-input`}
                disabled={disabled}
                isInvalid={isCareElementInvalid(element.Name)}
            />
        </div>
    );
}

function VitalSignTargetInput({
    dataCy,
    element
}: Readonly<{
    dataCy?: string;
    element: VitalSignsCareElementConfig;
}>): JSX.Element {
    if (element.Name === FlowsheetConstants.VitalSigns.BloodPressureCareElementName) {
        return <CareElementBloodPressureTargetInput dataCy={dataCy} element={element} />;
    }
    else {
        return <CareElementTargetInput dataCy={dataCy} element={element} />;
    }
}

function CareElementBloodPressureTargetInput({
    dataCy,
    element
}: Readonly<{
    dataCy?: string;
    element: VitalSignsCareElementConfig;
}>): JSX.Element {
    const { onEdit } = useContext(CareComponentConfigurationContext);

    return (
        <div className={styles.targetContainer}>
            <NumberInput
                value={element.TargetRange?.LowerBound}
                onChange={value => onEdit(element.Name, { ...element, TargetRange: { ...element.TargetRange, LowerBound: value } })}
                dataCy={`${dataCy}-systolic-input`}
            />
            <span>/</span>
            <NumberInput
                value={element.TargetRange?.UpperBound}
                onChange={value => onEdit(element.Name, { ...element, TargetRange: { ...element.TargetRange, UpperBound: value } })}
                dataCy={`${dataCy}-diastolic-input`}
            />
            <p className={styles.unitText}>{careElementUnitMapper[element.Name]}</p>
        </div>
    );
}

export { CareElementSimpleTargetInput, CareElementTargetInput, VitalSignTargetInput };
